import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PARCEL_STATUS_LABEL } from "../types";
import type {
  ParcelConflictAction,
  ParcelConflictDecision,
  ParcelConflictGroup,
} from "../types";

// Okno po „Przelicz działki (ULDK)" na zakładce mapy: przeliczenie znalazło działkę o
// INNYM numerze, której kształt nakłada się na działkę już w projekcie (podział / scalenie
// w ewidencji od poprzedniego przeliczenia). Zadanie NIE dodało jej — każda grupa nałożeń
// dostaje osobną decyzję użytkownika. Ta sama działka (ten sam TERYT) nigdy tu nie trafia.

interface Props {
  open: boolean;
  groups: ParcelConflictGroup[];
  onClose: () => void;
  onSubmit: (decisions: ParcelConflictDecision[]) => void;
  submitting?: boolean;
  error?: string | null;
}

const OPTIONS: { value: ParcelConflictAction; label: string; hint: string }[] = [
  {
    value: "keep_old",
    label: "Zostaw starą, nie dodawaj nowej",
    hint: "Nic się nie zmienia. Pytanie wróci przy kolejnym przeliczeniu.",
  },
  {
    value: "replace",
    label: "Dodaj nową i usuń starą",
    hint: "Stara działka znika z projektu razem z właścicielami, uzgodnieniami i historią.",
  },
  {
    value: "add_keep_unused",
    label: "Dodaj nową, stara zostaje jako niewykorzystywana",
    hint: "Stara działka zostaje na liście, oznaczona ręcznie jako niewykorzystywana w projekcie.",
  },
];

const fmtArea = (m2: number | null | undefined) =>
  m2 == null ? "" : `${Math.round(m2).toLocaleString("pl-PL")} m²`;

const fmtLen = (m: number | null | undefined) =>
  m == null || m < 0.01 ? "" : `trasa ${m.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} m`;

const joinParts = (...parts: string[]) => parts.filter(Boolean).join(", ");

export function ParcelConflictsModal({ open, groups, onClose, onSubmit, submitting, error }: Props) {
  const [choices, setChoices] = useState<Record<string, ParcelConflictAction>>({});
  useEffect(() => {
    if (open) setChoices({});
  }, [open, groups]);

  const allDecided = useMemo(
    () => groups.length > 0 && groups.every((g) => !!choices[g.id]),
    [groups, choices],
  );

  const submit = () => {
    if (!allDecided) return;
    onSubmit(
      groups.map((g) => ({
        existing_ids: g.existing.map((e) => e.id),
        incoming_teryts: g.incoming.map((i) => i.teryt),
        action: choices[g.id],
      })),
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Nakładanie się działek" size="lg">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="shrink-0 mt-0.5 text-amber-500" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Przeliczenie znalazło w ULDK{" "}
            {groups.length === 1 ? "działkę, która nakłada się" : "działki, które nakładają się"}{" "}
            na działki już obecne w projekcie (podział lub scalenie w ewidencji). Nie zostały
            dodane automatycznie — zdecyduj osobno dla każdej sytuacji.
          </p>
        </div>

        <ol className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {groups.map((g, idx) => (
            <li
              key={g.id}
              data-testid={`conflict-${g.id}`}
              className="border border-[var(--color-border)] p-3 space-y-2"
            >
              <p className="text-xs font-mono font-bold uppercase tracking-wider text-[var(--color-text-muted)]">
                Sytuacja {idx + 1} z {groups.length}
              </p>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-[var(--color-text-muted)]">Obecna w projekcie: </span>
                  {g.existing.map((e, i) => (
                    <span key={e.id}>
                      {i > 0 && ", "}
                      <span className="font-mono font-bold">{e.teryt}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {" "}
                        (
                        {joinParts(
                          fmtArea(e.area_m2),
                          PARCEL_STATUS_LABEL[e.status] ?? e.status,
                          e.owners_count > 0
                            ? `${e.owners_count} ${e.owners_count === 1 ? "właściciel" : "właścicieli"}`
                            : "",
                          e.has_progress ? "postęp pozyskania" : "",
                          e.is_manual ? "ręczna" : "",
                        )}
                        )
                      </span>
                    </span>
                  ))}
                </p>
                <p>
                  <span className="text-[var(--color-text-muted)]">Nowa z ULDK: </span>
                  {g.incoming.map((n, i) => (
                    <span key={n.teryt}>
                      {i > 0 && ", "}
                      <span className="font-mono font-bold">{n.teryt}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {" "}
                        (
                        {joinParts(
                          fmtArea(n.area_m2),
                          fmtLen(n.intersection_length_m),
                          n.in_project ? "już w projekcie" : "",
                        )}
                        )
                      </span>
                    </span>
                  ))}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Nałożenie: {fmtArea(g.overlap_m2)}
                </p>
              </div>
              <div className="space-y-1.5 pt-1">
                {OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name={`conflict-${g.id}`}
                      className="mt-1"
                      checked={choices[g.id] === o.value}
                      onChange={() => setChoices((c) => ({ ...c, [g.id]: o.value }))}
                    />
                    <span>
                      {o.label}
                      <span className="block text-[11px] text-[var(--color-text-muted)] leading-snug">
                        {o.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ol>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Później
          </Button>
          <Button onClick={submit} disabled={!allDecided} loading={submitting}>
            Zastosuj decyzje
          </Button>
        </div>
      </div>
    </Modal>
  );
}
